/**
 * APPLICATION:THEG_MOVIES_LIBRARY dedicated shell.
 * Header + workspace + five-tab bottom nav (HOME|MOVIES|SEARCH|CHATGPT|JOBS).
 * Infrastructure/status/profile via top-right menu (canon §2 / §3) — not a sixth tab.
 * Member/project/infra resolve via RUNYI assignments/catalog only — never hardcoded.
 * Step4: account-switch isolation clears session/storage/backend target on member change.
 */
"use client";

import { useCallback, useState } from "react";
import { ThegMoviesBottomNav } from "./ThegMoviesBottomNav";
import { ThegAccountSwitchGuard } from "./ThegAccountSwitchGuard";
import { isolateForAccountSwitch } from "./theg-account-switch-isolation";

export function ThegMoviesShell({
  memberLabel,
  memberKey,
  projectRef,
  assignmentSlot,
  onAccountSwitch,
  children,
}: {
  memberLabel?: string;
  memberKey?: string | null;
  /** RUNYI-resolved tenant project ref (backend target). */
  projectRef?: string | null;
  assignmentSlot?: string | null;
  /** Host callback after local isolation — should re-auth / re-resolve member. */
  onAccountSwitch?: () => void;
  children: React.ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSwitchAccount = useCallback(() => {
    isolateForAccountSwitch();
    setMenuOpen(false);
    onAccountSwitch?.();
  }, [onAccountSwitch]);

  return (
    <div
      className="flex min-h-dvh flex-col bg-background text-foreground"
      data-application="APPLICATION:THEG_MOVIES_LIBRARY"
      data-theg-project-ref={projectRef ?? undefined}
    >
      <ThegAccountSwitchGuard
        memberKey={memberKey}
        projectRef={projectRef}
        assignmentSlot={assignmentSlot}
      />
      <header className="relative flex h-12 items-center justify-between border-b border-border/40 px-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold tracking-tight">THEG MOVIES LIBRARY</p>
          {memberLabel ? (
            <p className="truncate text-[11px] text-foreground/50">{memberLabel}</p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label="Application menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="grid size-9 place-items-center rounded-md text-lg text-foreground/70"
        >
          ⋯
        </button>
        {menuOpen ? (
          <div
            role="menu"
            className="absolute right-2 top-11 z-50 min-w-[10rem] rounded-md border border-border/50 bg-background p-1 shadow-lg"
          >
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded px-3 py-2 text-left text-xs font-medium text-foreground/80 hover:bg-foreground/5"
              onClick={handleSwitchAccount}
            >
              Switch account
            </button>
          </div>
        ) : null}
      </header>
      <main className="flex-1 overflow-auto pb-16">{children}</main>
      <ThegMoviesBottomNav />
    </div>
  );
}
